import { $http } from '@/common/api/http.service';
import { useAuthStore } from '@/stores/auth';

const SAVE_FAILED = 'The form could not be saved.';

// A plain resource for the request body: fhirworks models serialize through toJSON(), plain objects
// pass through as they are.
const toBody = (form) => (typeof form?.toJSON === 'function' ? form.toJSON() : form);

// The server's refusal, with its violations ([{ path, rule }]) and status attached so a caller can
// tell a refused change from a failed request and show the reason.
function refusal(error) {
    const response = error?.response;
    const data = response?.data;
    // The app always answers in JSON. Anything else came from in front of it, such as the edge
    // WAF's HTML 403 (#6418): the user still sees the generic failure, so name the cause here.
    const contentType = response?.headers?.['content-type'] || '';
    if (response && (typeof data === 'string' || (contentType && !/json/i.test(contentType)))) {
        const request = `${(error.config?.method || '').toUpperCase()} ${error.config?.url || ''}`.trim();
        console.error(
            `Questionnaire request ${request} was refused before reaching the app: HTTP ${response.status} (${contentType || 'no content type'}). An edge block such as the WAF answers this way.`,
        );
    }
    const refused = new Error(data?.error || SAVE_FAILED);
    refused.status = error?.response?.status;
    refused.violations = Array.isArray(data?.violations) ? data.violations : [];
    refused.cause = error;
    return refused;
}

/**
 * Writes to a non-draft form (#6228). Aidbox accepts direct writes for drafts and autonotes only; a
 * published, inactive or Locked form is changed through these endpoints, which check the change
 * against the stored form. Every method throws on a 4xx/5xx with the server's violations attached.
 */
export function useQuestionnaireApi() {
    const authStore = useAuthStore();

    const formUrl = (id) => `/api/account/${authStore.account.id}/questionnaire/${id}`;

    /** PUT the full form. Resolves to the stored resource. */
    async function saveQuestionnaire(form) {
        try {
            const response = await $http.put(formUrl(form.id), toBody(form), { errorHandle: false });
            return response.data?.resource;
        } catch (error) {
            throw refusal(error);
        }
    }

    /** Publish a draft or reactivate an inactive form. Resolves to { resource, locked: [ids] }. */
    async function publishQuestionnaire(id) {
        try {
            const response = await $http.post(`${formUrl(id)}/publish`, {}, { errorHandle: false });
            return response.data;
        } catch (error) {
            throw refusal(error);
        }
    }

    /** Carry Field Library values into a published form. Resolves to { resource, snapshotId }. */
    async function reviseQuestionnaire(form) {
        try {
            const response = await $http.post(`${formUrl(form.id)}/revise`, toBody(form), { errorHandle: false });
            return response.data;
        } catch (error) {
            throw refusal(error);
        }
    }

    return { saveQuestionnaire, publishQuestionnaire, reviseQuestionnaire };
}
